/**
 * TR IBAN doğrulama + gösterim formatı.
 *
 * `handoff/locale-tr.md § 4`:
 *   TR IBAN 26 karakter — "TR" + 24 hane. Gösterim 4'erli grup:
 *     TR12 3456 7890 1234 5678 9012 34
 *
 * Mod-97 checksum (ISO 13616) zorunlu validasyon değil ama tercih edilir
 * (form input'ta typo yakalar). MVP'de format + uzunluk + numeric yeterli.
 */

const TR_IBAN_REGEX = /^TR\d{24}$/;

/**
 * TR prefix + 24 numerik hane.
 * Mod-97 checksum opsiyonel — daha sıkı validasyon için aşağıdaki helper.
 */
export function isValidIBANFormat(raw: string): boolean {
  const cleaned = raw.replace(/\s+/g, '').toUpperCase();
  return TR_IBAN_REGEX.test(cleaned);
}

/**
 * ISO 13616 Mod-97 checksum doğrulaması.
 * Format geçerse + checksum geçerse true.
 */
export function isValidIBAN(raw: string): boolean {
  const cleaned = raw.replace(/\s+/g, '').toUpperCase();
  if (!TR_IBAN_REGEX.test(cleaned)) return false;
  // ISO 13616 — ülke + checksum rotate edilir, sayıya çevrilir, mod 97 = 1
  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);
  const numeric = rearranged
    .split('')
    .map((ch) => {
      if (ch >= '0' && ch <= '9') return ch;
      // Harf: A=10, B=11, ..., Z=35
      return String(ch.charCodeAt(0) - 'A'.charCodeAt(0) + 10);
    })
    .join('');
  // BigInt — 30+ haneli sayı için
  return BigInt(numeric) % 97n === 1n;
}

/**
 * "TR330006100519786457841326" → "TR33 0006 1005 1978 6457 8413 26"
 * Geçersiz input ise raw döner (format değiştirme).
 */
export function formatIBAN(raw: string): string {
  const cleaned = raw.replace(/\s+/g, '').toUpperCase();
  if (!TR_IBAN_REGEX.test(cleaned)) return raw;
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}

/**
 * IBAN'ı maskeleme — log/UI için (son 4 hane görünür).
 * @example maskIBAN("TR330006100519786457841326") === "TR** **** **** **** **** ** 1326"
 */
export function maskIBAN(raw: string): string {
  const cleaned = raw.replace(/\s+/g, '').toUpperCase();
  if (!TR_IBAN_REGEX.test(cleaned)) return raw;
  return 'TR' + '*'.repeat(20) + cleaned.slice(-4);
}
